import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { WaitlistCTA } from '../components/WaitlistCTA';
import { Plus, Search, SlidersHorizontal, ChevronDown, CheckCircle2, ArrowUp, Monitor, Smartphone } from 'lucide-react';
import { DottedLogo } from '../components/DottedLogo';
import { UseCaseCrossLinks } from '../components/UseCaseCrossLinks';
import { UseCaseTestimonials } from '../components/UseCaseTestimonials';
import { ClosingCTA } from '../components/ClosingCTA';
const TESTIMONIALS = [{
  name: 'Sam Whitfield',
  role: 'Founder, design studio',
  avatar: 'https://i.pravatar.cc/100?img=13',
  quote: 'I walk into every client call already knowing the open loops, the blockers, and the last thing we promised.'
}, {
  name: 'Elena Torres',
  role: 'Account Director, agency',
  avatar: 'https://i.pravatar.cc/100?img=32',
  quote: 'Giga chases overdue invoices for me. Polite reminders go out on time and I stopped dreading the money conversation.'
}, {
  name: 'Marcus Hale',
  role: 'Freelance consultant',
  avatar: 'https://i.pravatar.cc/100?img=53',
  quote: 'Requests come in over email, Slack, and texts. Giga turns all of it into one queue so nothing falls through.'
}];
const ROTATING_CLAIMS = ['Prep every client call', 'Get paid faster', 'Never drop a follow-up', 'Close every open loop'];
const FEATURED_APPS = [{
  slug: 'gmail',
  label: 'Gmail'
}, {
  slug: 'google-calendar',
  label: 'Calendar'
}, {
  slug: 'google-drive',
  label: 'Drive'
}, {
  slug: 'imessage',
  label: 'iMessage'
}, {
  slug: 'whatsapp',
  label: 'WhatsApp'
}, {
  slug: 'quickbooks',
  label: 'QuickBooks'
}];
type Source = {
  slug: string;
  name: string;
  desc: string;
};
type Step = {
  id: string;
  label: string;
  sessionTitle: string;
  prompt: string;
  sources: Source[];
  briefTitle: string;
  briefLines: string[];
};
const STEPS: Step[] = [{
  id: 'prep-brief',
  label: 'Prep Brief',
  sessionTitle: 'Prep Meridian check-in',
  prompt: 'Prep me for my Meridian client call at 2',
  sources: [{
    slug: 'google-calendar',
    name: 'Google Calendar',
    desc: '2 PM today, Meridian weekly check-in with Priya and Ken'
  }, {
    slug: 'gmail',
    name: 'Gmail',
    desc: 'Find the latest Meridian threads and open questions'
  }, {
    slug: 'google-drive',
    name: 'Google Drive',
    desc: "Open proposal, scope doc, and last week's notes"
  }, {
    slug: 'slack',
    name: 'Slack',
    desc: 'Check #meridian-client for recent approvals and blockers'
  }],
  briefTitle: 'Here is the Meridian brief for 2 PM:',
  briefLines: ['Phase 2 scope was approved in Slack on Tuesday — confirm the start date.', 'Priya flagged the analytics dashboard as the top priority this week.', 'Last invoice (#1042) is 6 days overdue — gently raise it at the end.']
}, {
  id: 'track-requests',
  label: 'Track Requests',
  sessionTitle: 'Open client requests',
  prompt: 'What is everyone waiting on me for?',
  sources: [{
    slug: 'gmail',
    name: 'Gmail',
    desc: 'Scan client threads for unanswered asks'
  }, {
    slug: 'slack',
    name: 'Slack',
    desc: 'Pull open mentions across client channels'
  }, {
    slug: 'linear',
    name: 'Linear',
    desc: 'Cross-reference tickets tagged client-blocked'
  }],
  briefTitle: '5 open requests across 3 clients:',
  briefLines: ['Meridian — waiting on revised mockups (asked 2 days ago).', 'Atlas — needs the updated SOW before Friday.', 'Northstar — follow up on the data export they requested.']
}, {
  id: 'billing-follow-up',
  label: 'Billing Follow-Up',
  sessionTitle: 'Chase overdue invoices',
  prompt: 'Which invoices are overdue and who do I nudge?',
  sources: [{
    slug: 'quickbooks',
    name: 'QuickBooks',
    desc: 'List invoices past their due date'
  }, {
    slug: 'gmail',
    name: 'Gmail',
    desc: 'Draft polite payment reminders per client'
  }],
  briefTitle: '$14,200 outstanding across 2 invoices:',
  briefLines: ['Meridian #1042 — $8,400, 6 days overdue. Draft reminder ready.', 'Atlas #1051 — $5,800, due tomorrow. Send a friendly heads-up.']
}, {
  id: 'close-open-loops',
  label: 'Close Open Loops',
  sessionTitle: 'Wrap up loose ends',
  prompt: 'Close out anything still hanging from last week',
  sources: [{
    slug: 'gmail',
    name: 'Gmail',
    desc: 'Find threads with no reply in 5+ days'
  }, {
    slug: 'google-calendar',
    name: 'Google Calendar',
    desc: 'Spot meetings with no follow-up sent'
  }],
  briefTitle: '3 loops ready to close:',
  briefLines: ['Send recap + action items from the Atlas launch call.', 'Reply to Northstar with the timeline they asked about.', 'Confirm next check-in date with Meridian.']
}, {
  id: 'scope-change',
  label: 'Scope Change',
  sessionTitle: 'Handle a scope request',
  prompt: 'Meridian wants to add 2 more screens — what changes?',
  sources: [{
    slug: 'google-drive',
    name: 'Google Drive',
    desc: 'Open the current scope doc and estimate'
  }, {
    slug: 'slack',
    name: 'Slack',
    desc: 'Pull the thread where the new ask came in'
  }],
  briefTitle: 'Scope impact summary:',
  briefLines: ['Adds ~12 hours — roughly +$1,800 at the current rate.', 'Pushes delivery out by 3 business days.', 'Draft a change order for Priya to approve.']
}];
function GeneratingStep({
  icon,
  text,
  index
}: {
  icon: string;
  text: string;
  index: number;
}) {
  const reduce = useReducedMotion();
  const words = text.split(' ');
  return <div className="flex items-start gap-3" data-id="element-6281" data-mp="3ry44dto0m026">
      <div className="relative w-6 h-6 shrink-0 mt-0.5" data-id="element-6282" data-mp="kpze8utzs11">
        <motion.div initial={{
        scale: 0.5,
        opacity: 0
      }} whileInView={{
        scale: 1,
        opacity: 1
      }} viewport={{
        once: true,
        margin: '-50px'
      }} transition={{
        type: 'spring',
        stiffness: 300,
        damping: 20,
        delay: reduce ? 0 : index * 0.25
      }} className="absolute inset-0 rounded bg-white/10 flex items-center justify-center z-10" data-id="element-6283" data-mp="l9i3rsb2kg4">
          <AppIcon slug={icon} className="w-3.5 h-3.5 object-contain" data-id="element-6284" data-mp="2alklai1q1z5v" />
        </motion.div>
        {!reduce && <motion.div initial={{
        opacity: 0,
        scale: 0.8
      }} whileInView={{
        opacity: [0, 1, 0],
        scale: [0.8, 1.4, 1.6]
      }} viewport={{
        once: true,
        margin: '-50px'
      }} transition={{
        duration: 0.8,
        delay: index * 0.25,
        ease: 'easeOut'
      }} className="absolute inset-0 rounded border border-[#EA580C]/40" data-id="element-6285" data-mp="25mpuastlp4ao" />}
      </div>

      <p className="text-sm text-neutral-300 leading-relaxed flex flex-wrap gap-x-[0.25em]" data-id="element-6286" data-mp="2pewbu68bey2i">
        {words.map((word, wIdx) => <motion.span key={wIdx} initial={{
        opacity: 0,
        y: 4
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-50px'
      }} transition={{
        duration: 0.2,
        delay: reduce ? 0 : index * 0.25 + 0.1 + wIdx * 0.02
      }} className="inline-block" data-id="element-6287" data-mp="2ycq3v4z1r5jv" data-mpi={wIdx}>
            {word}
          </motion.span>)}
      </p>
    </div>;
}
function AppIcon({
  slug,
  className
}: {
  slug: string;
  className?: string;
}) {
  const src = slug === 'imessage' ? 'https://cdn.simpleicons.org/imessage' : `https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/${slug}.svg`;
  return <img src={src} alt="" aria-hidden="true" className={className} onError={e => {
    if (!e.currentTarget.src.includes('simpleicons')) {
      e.currentTarget.src = `https://cdn.simpleicons.org/${slug}`;
    } else {
      e.currentTarget.style.visibility = 'hidden';
    }
  }} data-id="element-6288" data-mp="3au0vjgx5mzjc" />;
}
function ProductWindow({
  step,
  device
}: {
  step: Step;
  device: 'desktop' | 'mobile';
}) {
  const isMobile = device === 'mobile';
  return <motion.div layout initial={false} animate={{
    width: isMobile ? 360 : '100%',
    borderRadius: isMobile ? 36 : 16
  }} transition={{
    type: 'spring',
    bounce: 0.2,
    duration: 0.6
  }} className="relative mx-auto bg-[#FAF8F4] border border-[#EAE4D9] shadow-2xl shadow-orange-900/10 overflow-hidden flex flex-col" data-id="element-6289" data-mp="l6id8imj8xu2">
      {/* Phone Notch (only visible in mobile) */}
      <AnimatePresence data-id="element-6290" data-mp="354ng959mdjdq">
        {isMobile && <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-6 bg-black rounded-b-2xl z-50 shadow-sm" data-id="element-6291" data-mp="30bassygofimk" />}
      </AnimatePresence>

      {/* Giga-branded Header */}
      <div className={`relative flex items-center justify-between px-5 bg-[#1A1410] border-b border-black/20 z-40 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] ${isMobile ? 'h-14 pt-4' : 'h-12'}`} data-id="element-6292" data-mp="2mahyoeuaepjz">
        <div className="flex items-center gap-2" data-id="element-6293" data-mp="2cfg9xk3xbphx">
          <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="Giga" className="w-4 h-4 object-contain [filter:brightness(0)_saturate(100%)_invert(60%)_sepia(60%)_saturate(2500%)_hue-rotate(346deg)_brightness(100%)_contrast(96%)]" data-id="element-6294" data-mp="fld28wvl6xk9" />
          <span className="text-xs font-bold text-white tracking-wide" data-id="element-6295" data-mp="n3tcaa7gr917">
            Giga Workspace
          </span>
        </div>
        <div className="flex items-center gap-3 text-neutral-400" data-id="element-6296" data-mp="rxxkewsgtn3v">
          <div className="w-2 h-2 rounded-full bg-[#EA580C] animate-pulse shadow-[0_0_8px_rgba(234,88,12,0.6)]" data-id="element-6297" data-mp="wtmfamtjiftu" />
          <span className="text-[10px] font-mono uppercase tracking-widest" data-id="element-6298" data-mp="3vee4q0a1cf5o">
            Active
          </span>
        </div>
      </div>

      <div className="flex h-[460px] md:h-[520px]" data-id="element-6299" data-mp="95mnrkoemle7">
        {/* Sidebar */}
        <AnimatePresence data-id="element-6300" data-mp="97ft8wna6zkm">
          {!isMobile && <motion.div initial={{
          width: 0,
          opacity: 0
        }} animate={{
          width: 224,
          opacity: 1
        }} exit={{
          width: 0,
          opacity: 0
        }} className="hidden sm:flex shrink-0 flex-col gap-4 border-r border-[#EAE4D9] bg-[#FCFBF9] p-4 z-20" data-id="element-6301" data-mp="e4pud9k18zoa">
              <button className="flex items-center justify-center gap-2 rounded-xl bg-white border border-[#EAE4D9] hover:border-[#D4D0C8] hover:bg-neutral-50 transition-all py-2.5 text-sm font-bold text-[#2B211A] shadow-sm" data-id="element-6302" data-mp="3h9chan893ic4">
                <Plus size={16} className="text-[#EA580C]" data-id="element-6303" data-mp="3q3hctjybxhxz" /> New Session
              </button>
              <div className="flex flex-col gap-1 mt-2" data-id="element-6304" data-mp="3v1af41wihrsz">
                <span className="text-[10px] font-bold uppercase tracking-wider text-neutral-400 mb-2 px-2" data-id="element-6305" data-mp="2yc1fauv8iasz">
                  Recent
                </span>
                {[{
              t: step.sessionTitle,
              time: 'Now',
              active: true
            }, {
              t: 'Atlas launch call',
              time: '3h',
              active: false
            }, {
              t: 'Northstar weekly update',
              time: '1d',
              active: false
            }].map((s, i) => <div key={i} className={`flex items-center justify-between rounded-lg px-3 py-2.5 text-sm transition-colors cursor-pointer ${s.active ? 'bg-white text-[#2B211A] font-bold border border-[#EAE4D9] shadow-sm' : 'text-neutral-500 hover:bg-white border border-transparent hover:border-[#EAE4D9] hover:shadow-sm'}`} data-id="element-6306" data-mp="rkah5twbqxiy" data-mpi={i}>
                    <span className="truncate" data-id="element-6307" data-mp="22bqak85d75ug">{s.t}</span>
                    {s.active && <span className="w-1.5 h-1.5 rounded-full bg-[#EA580C] shrink-0 ml-2" data-id="element-6308" data-mp="1xezw1p97kib5" />}
                  </div>)}
              </div>
            </motion.div>}
        </AnimatePresence>

        {/* Main panel */}
        <div className="flex-1 flex flex-col bg-[#FAF8F4] relative overflow-hidden shadow-[inset_1px_0_0_rgba(255,255,255,0.5)]" data-id="element-6309" data-mp="1ocicjikxu3ru">
          {/* Subtle background texture */}
          <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{
          backgroundImage: 'radial-gradient(#2B211A 1px, transparent 1px)',
          backgroundSize: '24px 24px'
        }} data-id="element-6310" data-mp="1efvo6jtekul2" />

          {/* Chat header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-[#EAE4D9] bg-white/80 backdrop-blur-md z-10 shadow-sm" data-id="element-6311" data-mp="38u6ekwpnbwsy">
            <h2 className="text-base md:text-lg font-title font-bold text-[#2B211A] flex items-center gap-2 truncate" data-id="element-6312" data-mp="1buo07i5sp7an">
              {step.sessionTitle}
            </h2>
            <div className="flex items-center gap-3 text-neutral-400 shrink-0" data-id="element-6313" data-mp="x3ymo94v3g3m">
              <button className="w-8 h-8 rounded-lg hover:bg-neutral-100 flex items-center justify-center transition-colors" data-id="element-6314" data-mp="125o0txnds9vx">
                <Search size={16} className="text-[#2B211A]" data-id="element-6315" data-mp="1psw4yc92pcwd" />
              </button>
              <button className="w-8 h-8 rounded-lg hover:bg-neutral-100 flex items-center justify-center transition-colors" data-id="element-6316" data-mp="1urpgw08povuf">
                <SlidersHorizontal size={16} className="text-[#2B211A]" data-id="element-6317" data-mp="1kvcd29jgd1ly" />
              </button>
            </div>
          </div>

          <AnimatedChatFlow step={step} data-id="element-6318" data-mp="5dspyh007v8y" />
        </div>
      </div>
    </motion.div>;
}
function AnimatedChatFlow({
  step
}: {
  step: Step;
}) {
  const reduce = useReducedMotion();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [phase, setPhase] = useState<'typing' | 'submitted' | 'sources' | 'summary'>('typing');
  const [typedChars, setTypedChars] = useState(0);
  const [visibleSources, setVisibleSources] = useState(0);
  const [visibleLines, setVisibleLines] = useState(0);
  // Keep the latest message in view as new content streams in, so the
  // conversation scrolls up inside the fixed window like a real chat.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({
      top: el.scrollHeight,
      behavior: reduce ? 'auto' : 'smooth'
    });
  }, [phase, visibleSources, visibleLines, reduce]);
  useEffect(() => {
    if (reduce) {
      setPhase('summary');
      setTypedChars(step.prompt.length);
      setVisibleSources(step.sources.length);
      setVisibleLines(step.briefLines.length);
      return;
    }
    // Reset state for new step
    setPhase('typing');
    setTypedChars(0);
    setVisibleSources(0);
    setVisibleLines(0);
    let isCancelled = false;
    const runSequence = async () => {
      // 1. Fast typing
      for (let i = 1; i <= step.prompt.length; i++) {
        if (isCancelled) return;
        setTypedChars(i);
        await new Promise(r => setTimeout(r, 15)); // 15ms per char
      }
      if (isCancelled) return;
      await new Promise(r => setTimeout(r, 200)); // Pause before submit
      // 2. Submit
      if (isCancelled) return;
      setPhase('submitted');
      await new Promise(r => setTimeout(r, 300)); // Pause before sources
      // 3. Stream sources
      if (isCancelled) return;
      setPhase('sources');
      for (let i = 1; i <= step.sources.length; i++) {
        if (isCancelled) return;
        setVisibleSources(i);
        await new Promise(r => setTimeout(r, 150)); // 150ms per source
      }
      if (isCancelled) return;
      await new Promise(r => setTimeout(r, 300)); // Pause before summary
      // 4. Stream summary
      if (isCancelled) return;
      setPhase('summary');
      for (let i = 1; i <= step.briefLines.length; i++) {
        if (isCancelled) return;
        setVisibleLines(i);
        await new Promise(r => setTimeout(r, 150)); // 150ms per line
      }
    };
    runSequence();
    return () => {
      isCancelled = true;
    };
  }, [step, reduce]);
  return <>
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 md:p-6 z-10 flex flex-col gap-6 md:gap-8 scroll-smooth" data-id="element-6319" data-mp="b0a51pri8ugm">
        {/* User prompt bubble */}
        {phase !== 'typing' && <motion.div initial={{
        opacity: 0,
        y: 10,
        scale: 0.95
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} transition={{
        duration: 0.2
      }} className="self-end max-w-[85%]" data-id="element-6320" data-mp="13oo7chpmmcxz">
            <div className="inline-flex items-center bg-white border border-[#EAE4D9] shadow-sm px-5 py-3 rounded-2xl rounded-tr-sm" data-id="element-6321" data-mp="up173z8mvd6i">
              <span className="text-[14px] md:text-[15px] text-[#2B211A] leading-snug" data-id="element-6322" data-mp="3fcnsfi8z6npq">
                {step.prompt}
              </span>
            </div>
          </motion.div>}

        {/* Tool sources */}
        {(phase === 'sources' || phase === 'summary') && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} className="flex flex-col gap-2 mb-2" data-id="element-6323" data-mp="3lqtf3cl89v1m">
            {step.sources.slice(0, visibleSources).map(src => <motion.div key={src.name} initial={{
          opacity: 0,
          x: -10
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.2
        }} className="flex items-center gap-3 p-2.5 rounded-xl bg-white border border-[#EAE4D9] shadow-sm w-fit max-w-[90%]" data-id="element-6324" data-mp="3bx2tqgkee0q5">
                <div className="w-8 h-8 rounded-lg bg-neutral-50 flex items-center justify-center border border-neutral-100 shrink-0" data-id="element-6325" data-mp="2t4i5jcfn8jso">
                  <AppIcon slug={src.slug} className="w-4 h-4 object-contain" data-id="element-6326" data-mp="2f5s6hr99ka1o" />
                </div>
                <div className="flex flex-col min-w-0 pr-2" data-id="element-6327" data-mp="2j59fgunwfpbl">
                  <span className="text-[13px] font-bold text-[#2B211A] truncate" data-id="element-6328" data-mp="20hqr9x8drtb9">
                    {src.name}
                  </span>
                  <span className="text-[11px] text-neutral-500 truncate" data-id="element-6329" data-mp="1vk8rawcccofe">
                    {src.desc}
                  </span>
                </div>
              </motion.div>)}
          </motion.div>}

        {/* Brief / Summary */}
        {phase === 'summary' && <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3
      }} className="flex gap-3 md:gap-4 max-w-3xl" data-id="element-6330" data-mp="2jgw8qodwg0r3">
            <div className="w-8 h-8 rounded-full bg-[#EA580C] flex items-center justify-center shrink-0 shadow-sm mt-0.5" data-id="element-6331" data-mp="2s9psoz4ajoba">
              <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="Giga" className="w-4 h-4 object-contain [filter:brightness(0)_saturate(100%)_invert(100%)]" data-id="element-6332" data-mp="2o8w6qbaesjl1" />
            </div>
            <div className="flex flex-col gap-4 text-[14px] md:text-[15px] text-[#2B211A] leading-relaxed pt-1" data-id="element-6333" data-mp="8g410pzluk6e">
              <p className="font-medium" data-id="element-6334" data-mp="cfmlxlrfev98">{step.briefTitle}</p>
              <ul className="space-y-3" data-id="element-6335" data-mp="hgacy7fnnc3g">
                {step.briefLines.slice(0, visibleLines).map((line, i) => {
              const parts = line.split(/(\*\*.*?\*\*)/g);
              return <motion.li key={i} initial={{
                opacity: 0,
                x: -5
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                duration: 0.2
              }} className="flex items-start gap-3" data-id="element-6336" data-mp="mcrattcnp4tq" data-mpi={i}>
                      <div className="w-1.5 h-1.5 rounded-full bg-[#EA580C]/40 shrink-0 mt-2" data-id="element-6337" data-mp="3usub3e1fvr81" />
                      <div data-id="element-6338" data-mp="3icc84u9be6f">
                        {parts.map((part, j) => {
                    if (part.startsWith('**') && part.endsWith('**')) {
                      return <strong key={j} className="font-bold text-[#2B211A]" data-id="element-6339" data-mp="374azopn3eybq">
                                {part.slice(2, -2)}
                              </strong>;
                    }
                    return <span key={j} className="text-neutral-700" data-id="element-6340" data-mp="2umgx3gxr5g7q" data-mpi={j}>
                              {part}
                            </span>;
                  })}
                      </div>
                    </motion.li>;
            })}
              </ul>
            </div>
          </motion.div>}
      </div>

      {/* Chat Composer */}
      <div className="p-4 border-t border-[#EAE4D9] bg-white z-10 shrink-0 shadow-[0_-4px_20px_rgba(0,0,0,0.02)]" data-id="element-6341" data-mp="2goaa4vucd0mu">
        <div className="flex items-center gap-3 bg-white border border-[#EAE4D9] rounded-2xl px-4 py-3 max-w-3xl mx-auto shadow-[0_2px_8px_rgba(0,0,0,0.04)] focus-within:border-[#EA580C] focus-within:ring-1 focus-within:ring-[#EA580C] transition-all" data-id="element-6342" data-mp="2bu636cl2sslr">
          <button className="w-6 h-6 rounded-full bg-neutral-100 flex items-center justify-center hover:bg-neutral-200 transition-colors shrink-0" data-id="element-6343" data-mp="1wxx4pd90ejae">
            <Plus size={14} className="text-neutral-500" data-id="element-6344" data-mp="1szr1gf7dqlu8" />
          </button>
          <div className="flex-1 text-[14px] md:text-[15px] text-[#2B211A] font-medium flex items-center h-6" data-id="element-6345" data-mp="htexxx7wtu6g">
            {phase === 'typing' ? <>
                {step.prompt.substring(0, typedChars)}
                <motion.span animate={{
              opacity: [1, 0]
            }} transition={{
              duration: 0.8,
              repeat: Infinity
            }} className="inline-block w-0.5 h-4 bg-[#EA580C] ml-0.5" data-id="element-6346" data-mp="8rfg52p2mm2g" />
              </> : <span className="text-neutral-400 font-normal" data-id="element-6347" data-mp="3qa07bf4gepa3">
                Ask Giga anything...
              </span>}
          </div>
          <div className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 transition-colors ${phase === 'typing' && typedChars > 0 ? 'bg-[#EA580C] text-white shadow-sm' : 'bg-neutral-100 text-neutral-400'}`} data-id="element-6348" data-mp="3lboviyulm4p8">
            <ArrowUp size={16} strokeWidth={2.5} data-id="element-6349" data-mp="3c97a2mmrpg3a" />
          </div>
        </div>
      </div>
    </>;
}
export function ClientManagement() {
  const [activeStep, setActiveStep] = useState(0);
  const [claimIndex, setClaimIndex] = useState(0);
  const [device, setDevice] = useState<'desktop' | 'mobile'>('desktop');
  const reduce = useReducedMotion();
  useEffect(() => {
    if (reduce) return;
    const id = setInterval(() => {
      setClaimIndex(i => (i + 1) % ROTATING_CLAIMS.length);
    }, 2600);
    return () => clearInterval(id);
  }, [reduce]);
  const step = STEPS[activeStep];
  return <>
      <section className="relative w-full bg-[#FAF8F4] text-[#2B211A] overflow-hidden" data-id="element-6350" data-mp="1446ss3s1ofm9">
        {/* Cream dot-grid texture */}
        <div aria-hidden="true" className="cream-grid z-0" data-id="element-6351" data-mp="1d321pnmh43t9" />

        <div className="relative z-10 max-w-[1200px] mx-auto px-6 pt-32 md:pt-40 pb-20 md:pb-28" data-id="element-6352" data-mp="1d1h1pihe3so0">
          {/* Eyebrow */}
          <div className="text-center" data-id="element-6353" data-mp="1hvleiidc6ux0">
            <span className="inline-block text-xs font-semibold tracking-[0.22em] uppercase text-neutral-400 mb-5" data-id="element-6354" data-mp="lcnqy09rb7lt">
              Client Management
            </span>

            {/* Headline */}
            <h1 className="font-title font-bold tracking-tight leading-[1.05] text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-[#2B211A]" data-id="element-6355" data-mp="1snlfvhuuhgnk">
              Focus on the work, not the admin
            </h1>

            {/* Rotating claim */}
            <div className="mt-4 h-[1.2em] font-title font-bold tracking-tight leading-[1.05] text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-[#EA580C]" data-id="element-6356" data-mp="22dddkky8qpw9">
              <AnimatePresence mode="wait" data-id="element-6357" data-mp="19opp6gi73keh">
                <motion.span key={claimIndex} initial={{
                opacity: 0,
                y: 14
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -14
              }} transition={{
                duration: 0.4,
                ease: [0.22, 1, 0.36, 1]
              }} className="block" data-id="element-6358" data-mp="19pr9lbl3pj8v">
                  {ROTATING_CLAIMS[claimIndex]}
                </motion.span>
              </AnimatePresence>
            </div>

            {/* CTA */}
            <div className="mt-12 flex flex-col items-center gap-4" data-id="element-6359" data-mp="2xn0jagwd79lp">
              <WaitlistCTA className="bg-[#2B211A] hover:bg-[#1A1410] text-white px-10 py-4 rounded-full font-bold text-lg transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-0.5" data-id="element-6360" data-mp="32md4kx7l88m6">
                Get Started
              </WaitlistCTA>
              <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-neutral-400" data-id="element-6361" data-mp="2sop9g662kz3h">
                Giga has a 7 day free trial.
              </p>
            </div>

            {/* Featured apps */}
            <div className="mt-16 pt-10 border-t border-[#EAE4D9] w-full max-w-4xl mx-auto" data-id="element-6362" data-mp="3lc2xtfvpcmbz">
              <p className="font-mono text-[10px] tracking-[0.28em] uppercase text-neutral-400 mb-6 text-center" data-id="element-6363" data-mp="36nf3fcxleinm">
                Featured Apps
              </p>
              <div className="flex flex-nowrap justify-start md:justify-center gap-3 md:gap-4 overflow-x-auto pb-4 -mx-6 px-6 md:mx-0 md:px-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-id="element-6364" data-mp="36zotk2m7q6a">
                {FEATURED_APPS.map(app => <div key={app.label} className="flex items-center gap-2.5 rounded-xl bg-white border border-[#EAE4D9] px-5 py-2.5 shadow-sm hover:shadow-md transition-shadow shrink-0" data-id="element-6365" data-mp="2gfsh83c55u06">
                    <AppIcon slug={app.slug} className="w-5 h-5 object-contain grayscale opacity-70" data-id="element-6366" data-mp="26hw6vdr13334" />
                    <span className="text-sm font-bold text-[#2B211A] whitespace-nowrap" data-id="element-6367" data-mp="21itbym4rid8v">
                      {app.label}
                    </span>
                  </div>)}
              </div>
            </div>
          </div>

          {/* Workflow tabs */}
          <div className="mt-24" data-id="element-6368" data-mp="2pfp9eexqpazz">
            <div className="flex items-center gap-2 overflow-x-auto border-b border-[#EAE4D9] pb-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-id="element-6369" data-mp="2penprm2nrg6n">
              {STEPS.map((s, i) => {
              const isActive = i === activeStep;
              return <button key={s.id} onClick={() => setActiveStep(i)} className={`relative flex items-center gap-2 whitespace-nowrap rounded-lg px-5 py-2.5 text-sm transition-colors ${isActive ? 'text-[#2B211A] font-bold bg-white border border-[#EAE4D9] shadow-sm' : 'text-neutral-500 hover:text-[#2B211A] hover:bg-white/50'}`} data-id="element-6370" data-mp="3hxuow746ozgh" data-mpi={i}>
                    {s.label}
                    {isActive && <motion.div layoutId="activeTabIndicator" className="absolute -bottom-4 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-[#EA580C]" data-id="element-6371" data-mp="3ud14hdu8oo4l" />}
                  </button>;
            })}

              <div className="ml-auto flex items-center gap-4 pl-4 shrink-0" data-id="element-6372" data-mp="86w0jde9q22q">
                {/* Device Toggle */}
                <div className="hidden md:flex items-center bg-white border border-[#EAE4D9] rounded-lg p-1 shadow-sm" data-id="element-6373" data-mp="c6wg51d3sjbd">
                  <button onClick={() => setDevice('desktop')} className={`p-1.5 rounded-md transition-colors ${device === 'desktop' ? 'bg-[#FAF8F4] text-[#2B211A] shadow-sm border border-[#EAE4D9]' : 'text-neutral-400 hover:text-[#2B211A]'}`} aria-label="Desktop view" data-id="element-6374" data-mp="lysp57gq9tud">
                    <Monitor size={16} data-id="element-6375" data-mp="vuvsggnbfyiw" />
                  </button>
                  <button onClick={() => setDevice('mobile')} className={`p-1.5 rounded-md transition-colors ${device === 'mobile' ? 'bg-[#FAF8F4] text-[#2B211A] shadow-sm border border-[#EAE4D9]' : 'text-neutral-400 hover:text-[#2B211A]'}`} aria-label="Mobile view" data-id="element-6376" data-mp="69vzorqc37xo">
                    <Smartphone size={16} data-id="element-6377" data-mp="b879j2gmpifw" />
                  </button>
                </div>

                <span className="font-mono text-xs font-bold text-neutral-400" data-id="element-6378" data-mp="oz2cjy4g55bk">
                  {activeStep + 1} / {STEPS.length}
                </span>
              </div>
            </div>

            {/* Product window */}
            <div className="mt-8" data-id="element-6379" data-mp="ywgtrzy71y4x">
              <ProductWindow step={step} device={device} data-id="element-6380" data-mp="ywqjdmfbu3a3" />
            </div>
          </div>

          {/* Giga Does the Work comparison section */}
          <div className="mt-32 md:mt-40" data-id="element-6381" data-mp="30od4j7cjf3yn">
            <div className="text-center mb-20" data-id="element-6382" data-mp="30p50wkdyl5pz">
              <span className="inline-block text-xs font-semibold tracking-[5px] uppercase text-neutral-400 mb-4" data-id="element-6383" data-mp="2vqblunj0x6np">
                Giga does the work
              </span>
              <h2 className="font-title font-bold text-4xl md:text-5xl text-[#2B211A]" data-id="element-6384" data-mp="1ba3r5aj191om">
                Other tools advise.{' '}
                <span className="text-[#EA580C]" data-id="element-6385" data-mp="11ig73uot2mx6">Giga executes.</span>
              </h2>
            </div>

            <div className="space-y-24" data-id="element-6386" data-mp="16g9akfyq52tr">
              {/* Row 1: CLIENT PREP */}
              <div className="flex flex-col gap-8" data-id="element-6387" data-mp="shhx8vrorl6o">
                <div className="max-w-3xl mx-auto" data-id="element-6388" data-mp="sfou3qga7j3f">
                  <div className="flex gap-3 md:gap-4" data-id="element-6389" data-mp="wjczc2hfy2li">
                    <span aria-hidden="true" className="font-title text-5xl md:text-6xl leading-[0.8] text-[#EA580C] select-none shrink-0" data-id="element-6390" data-mp="wgqrs2ujvn3a">
                      &ldquo;
                    </span>
                    <div className="pt-1" data-id="element-6391" data-mp="nif2c45f6vcf">
                      <h3 className="font-title font-bold text-2xl md:text-3xl text-[#2B211A] leading-snug" data-id="element-6392" data-mp="2cwldeuchr8vo">
                        I have a Meridian client call in 30 minutes. What should
                        I know?
                      </h3>
                      <p className="mt-2 text-sm font-medium text-neutral-400" data-id="element-6393" data-mp="14ojsxf49ntjq">
                        &mdash; You
                      </p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-10" data-id="element-6394" data-mp="vux0nyz9p8fa">
                  {/* Generic tool card */}
                  <div className="rounded-2xl bg-white border border-[#EAE4D9] shadow-sm p-6 md:p-8 flex flex-col" data-id="element-6395" data-mp="lylpjcbeyomf">
                    <div className="flex items-center gap-3 mb-6 pb-4 border-b border-[#EAE4D9]" data-id="element-6396" data-mp="gzheeg4xigtg">
                      <div className="w-6 h-6 rounded bg-neutral-100 flex items-center justify-center text-xs font-bold text-neutral-400" data-id="element-6397" data-mp="cxdqut4zjlrt">
                        ?
                      </div>
                      <span className="text-sm font-bold text-neutral-400 uppercase tracking-wider" data-id="element-6398" data-mp="27cq22281qoum">
                        Generic AI
                      </span>
                    </div>
                    <p className="text-base text-neutral-500 italic leading-relaxed font-serif" data-id="element-6399" data-mp="29ww9ijvvcxfx">
                      "Review your notes, check recent communication, prepare an
                      agenda, and make sure you understand the client's goals…"
                    </p>
                  </div>

                  {/* Giga card (Dark Theme) */}
                  <div className="rounded-2xl bg-[#2B211A] shadow-2xl shadow-orange-900/10 p-6 md:p-8 flex flex-col relative overflow-hidden" data-id="element-6400" data-mp="2okpkfra0fbs6">
                    <DottedLogo variant="dark" opacity={0.1} className="inset-0 scale-150" data-id="element-6401" data-mp="1vwlhvemqjetp" />
                    <div className="relative z-10 flex items-center gap-3 mb-6 pb-4 border-b border-white/10" data-id="element-6402" data-mp="20w6e3hait92q">
                      <div className="flex items-center gap-2" data-id="element-6403" data-mp="24y1fsmwd5oh1">
                        <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="" aria-hidden="true" className="w-5 h-5 object-contain [filter:brightness(0)_saturate(100%)_invert(60%)_sepia(60%)_saturate(2500%)_hue-rotate(346deg)_brightness(100%)_contrast(96%)]" data-id="element-6404" data-mp="24v6tsqxpbvli" />
                        <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/giga4x.png" alt="Giga" className="h-3.5 object-contain [filter:brightness(0)_invert(1)]" data-id="element-6405" data-mp="g7nfdlyylvmg" />
                      </div>
                      <span className="ml-auto flex h-2.5 w-2.5 relative" data-id="element-6406" data-mp="3omyp4a905ahc">
                        <span className="absolute inline-flex h-full w-full rounded-full bg-[#EA580C] opacity-75 animate-ping" data-id="element-6407" data-mp="3tnmgwy521vcm" />
                        <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#EA580C]" data-id="element-6408" data-mp="3er43d69hezac" />
                      </span>
                    </div>
                    <div className="relative z-10 space-y-4 mb-8 flex-1" data-id="element-6409" data-mp="1cypcvisvgmib">
                      {[{
                      icon: 'google-calendar',
                      text: 'Opening your 2 PM Meridian client check-in'
                    }, {
                      icon: 'gmail',
                      text: 'Pulling the latest email thread, open questions, and promised follow-ups'
                    }, {
                      icon: 'google-drive',
                      text: 'Found the SOW, last call notes, and revised pricing page wireframe'
                    }, {
                      icon: 'slack',
                      text: 'Their launch date is steady, but copy approval is now the blocker'
                    }, {
                      icon: 'gmail',
                      text: 'Drafting a recap template before the call starts'
                    }].map((step, i) => <GeneratingStep key={i} icon={step.icon} text={step.text} index={i} data-id="element-6410" data-mp="3v25zp9ndcysd" data-mpi={i} />)}
                    </div>
                    <div className="relative z-10 bg-[#EA580C] rounded-2xl p-6 md:p-8 mt-auto shadow-xl shadow-orange-900/30" data-id="element-6411" data-mp="20q7pp5h8jyvf">
                      <span className="block text-[11px] font-bold uppercase tracking-[0.2em] text-white/80 mb-3" data-id="element-6412" data-mp="1vro5pam7cx5p">
                        The payoff
                      </span>
                      <p className="text-xl md:text-2xl font-bold text-white leading-snug" data-id="element-6413" data-mp="4n2vxj6nl6i8">
                        You walk into the call with the account history, the
                        open loops, and the next move already lined up.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Row 2: REQUEST TRIAGE */}
              <div className="flex flex-col gap-8" data-id="element-6414" data-mp="nlp1ba97djry">
                <div className="max-w-3xl mx-auto" data-id="element-6415" data-mp="nkdzhfzbim3h">
                  <div className="flex gap-3 md:gap-4" data-id="element-6416" data-mp="scx7zyj5er1f">
                    <span aria-hidden="true" className="font-title text-5xl md:text-6xl leading-[0.8] text-[#EA580C] select-none shrink-0" data-id="element-6417" data-mp="sdgrvksczpi3">
                      &ldquo;
                    </span>
                    <div className="pt-1" data-id="element-6418" data-mp="11f6jaqm8qofv">
                      <h3 className="font-title font-bold text-2xl md:text-3xl text-[#2B211A] leading-snug" data-id="element-6419" data-mp="16c6okzb9nn5x">
                        What client requests came in today and which ones
                        matter?
                      </h3>
                      <p className="mt-2 text-sm font-medium text-neutral-400" data-id="element-6420" data-mp="1bfpdorpo71wm">
                        &mdash; You
                      </p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-10" data-id="element-6421" data-mp="12iezoylgwyo0">
                  {/* Generic tool card */}
                  <div className="rounded-2xl bg-white border border-[#EAE4D9] shadow-sm p-6 md:p-8 flex flex-col" data-id="element-6422" data-mp="1u7rxgbxlyyb3">
                    <div className="flex items-center gap-3 mb-6 pb-4 border-b border-[#EAE4D9]" data-id="element-6423" data-mp="1pgjnnesd9axe">
                      <div className="w-6 h-6 rounded bg-neutral-100 flex items-center justify-center text-xs font-bold text-neutral-400" data-id="element-6424" data-mp="1lfzs2fnfyfdd">
                        N
                      </div>
                      <span className="text-sm font-bold text-neutral-400 uppercase tracking-wider" data-id="element-6425" data-mp="2d6w2zapxy9y0">
                        Notion
                      </span>
                    </div>
                    <p className="text-base text-neutral-500 italic leading-relaxed font-serif" data-id="element-6426" data-mp="22npkxabk5gzf">
                      "Create a database with client name, request, priority,
                      deadline, owner, and status. Update it consistently after
                      each message…"
                    </p>
                  </div>

                  {/* Giga card (Dark Theme) */}
                  <div className="rounded-2xl bg-[#2B211A] shadow-2xl shadow-orange-900/10 p-6 md:p-8 flex flex-col relative overflow-hidden" data-id="element-6427" data-mp="2ghgl3s3efoih">
                    <DottedLogo variant="dark" opacity={0.1} className="inset-0 scale-150" data-id="element-6428" data-mp="27nlpbao63afi" />
                    <div className="relative z-10 flex items-center gap-3 mb-6 pb-4 border-b border-white/10" data-id="element-6429" data-mp="2bnm44wf6hnn3">
                      <div className="flex items-center gap-2" data-id="element-6430" data-mp="2zcx78kk03u4e">
                        <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="" aria-hidden="true" className="w-5 h-5 object-contain [filter:brightness(0)_saturate(100%)_invert(60%)_sepia(60%)_saturate(2500%)_hue-rotate(346deg)_brightness(100%)_contrast(96%)]" data-id="element-6431" data-mp="344gbkcl59i6o" />
                        <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/giga4x.png" alt="Giga" className="h-3.5 object-contain [filter:brightness(0)_invert(1)]" data-id="element-6432" data-mp="12ercvadtdubx" />
                      </div>
                      <span className="ml-auto flex h-2.5 w-2.5 relative" data-id="element-6433" data-mp="4tbrebtx878y">
                        <span className="absolute inline-flex h-full w-full rounded-full bg-[#EA580C] opacity-75 animate-ping" data-id="element-6434" data-mp="jq2rknbf5mzm" />
                        <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#EA580C]" data-id="element-6435" data-mp="eon5qt1f3cg6" />
                      </span>
                    </div>
                    <div className="relative z-10 space-y-4 mb-8 flex-1" data-id="element-6436" data-mp="1q5mzzu7bs1cu">
                      {[{
                      icon: 'gmail',
                      text: 'Scanning unread client email from the last 24 hours'
                    }, {
                      icon: 'slack',
                      text: 'Checking shared client channels for asks and approvals'
                    }, {
                      icon: 'whatsapp',
                      text: 'Finding client texts with approvals and links that never hit email'
                    }, {
                      icon: 'slack',
                      text: 'Checking group chats for decisions buried in side threads'
                    }, {
                      icon: 'linear',
                      text: 'Logging 12 asks by client, channel, urgency, owner, and due date'
                    }, {
                      icon: 'gmail',
                      text: 'Drafting replies for the 4 that need attention today'
                    }].map((step, i) => <GeneratingStep key={i} icon={step.icon} text={step.text} index={i} data-id="element-6437" data-mp="10pvx67idn6d6" data-mpi={i} />)}
                    </div>
                    <div className="relative z-10 bg-[#EA580C] rounded-2xl p-6 md:p-8 mt-auto shadow-xl shadow-orange-900/30" data-id="element-6438" data-mp="3dzngq97vot12">
                      <span className="block text-[11px] font-bold uppercase tracking-[0.2em] text-white/80 mb-3" data-id="element-6439" data-mp="39zvba5ds72zx">
                        The payoff
                      </span>
                      <p className="text-xl md:text-2xl font-bold text-white leading-snug" data-id="element-6440" data-mp="3004fm0dhihy9">
                        Email, chat, and group texts turn into one prioritized
                        queue before the next client call lands.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Row 3: BILLING */}
              <div className="flex flex-col gap-8" data-id="element-6441" data-mp="2hbh8lcbqzii1">
                <div className="max-w-3xl mx-auto" data-id="element-6442" data-mp="2cbboqibpz0ai">
                  <div className="flex gap-3 md:gap-4" data-id="element-6443" data-mp="2ca0b7qsh2e67">
                    <span aria-hidden="true" className="font-title text-5xl md:text-6xl leading-[0.8] text-[#EA580C] select-none shrink-0" data-id="element-6444" data-mp="27ciagnolz59a">
                      &ldquo;
                    </span>
                    <div className="pt-1" data-id="element-6445" data-mp="42db548p1vlb">
                      <h3 className="font-title font-bold text-2xl md:text-3xl text-[#2B211A] leading-snug" data-id="element-6446" data-mp="41c1933ezf56">
                        Which invoices are overdue and who do I nudge?
                      </h3>
                      <p className="mt-2 text-sm font-medium text-neutral-400" data-id="element-6447" data-mp="3r5rv5e3tb8at">
                        &mdash; You
                      </p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-10" data-id="element-6448" data-mp="3hagegq8ufr0f">
                  {/* Generic tool card */}
                  <div className="rounded-2xl bg-white border border-[#EAE4D9] shadow-sm p-6 md:p-8 flex flex-col" data-id="element-6449" data-mp="38guzp6woi9r0">
                    <div className="flex items-center gap-3 mb-6 pb-4 border-b border-[#EAE4D9]" data-id="element-6450" data-mp="2tj2lqj6s5tex">
                      <div className="w-6 h-6 rounded bg-neutral-100 flex items-center justify-center text-xs font-bold text-neutral-400" data-id="element-6451" data-mp="2yj73ecjfcu6q">
                        ?
                      </div>
                      <span className="text-sm font-bold text-neutral-400 uppercase tracking-wider" data-id="element-6452" data-mp="2avjzhrato7h8">
                        Generic AI
                      </span>
                    </div>
                    <p className="text-base text-neutral-500 italic leading-relaxed font-serif" data-id="element-6453" data-mp="2jvyph3yqear2">
                      "Check your accounting software for overdue invoices, then
                      draft polite payment reminders for each client…"
                    </p>
                  </div>

                  {/* Giga card (Dark Theme) */}
                  <div className="rounded-2xl bg-[#2B211A] shadow-2xl shadow-orange-900/10 p-6 md:p-8 flex flex-col relative overflow-hidden" data-id="element-6454" data-mp="37k851o5cq29r">
                    <DottedLogo variant="dark" opacity={0.1} className="inset-0 scale-150" data-id="element-6455" data-mp="3hjql3ti553wj" />
                    <div className="relative z-10 flex items-center gap-3 mb-6 pb-4 border-b border-white/10" data-id="element-6456" data-mp="3hhcqflrdhtpi">
                      <div className="flex items-center gap-2" data-id="element-6457" data-mp="3ka6hx6nsdntu">
                        <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="" aria-hidden="true" className="w-5 h-5 object-contain [filter:brightness(0)_saturate(100%)_invert(60%)_sepia(60%)_saturate(2500%)_hue-rotate(346deg)_brightness(100%)_contrast(96%)]" data-id="element-6458" data-mp="3kag8owgnnz0l" />
                        <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/giga4x.png" alt="Giga" className="h-3.5 object-contain [filter:brightness(0)_invert(1)]" data-id="element-6459" data-mp="6u8tvraiyryp" />
                      </div>
                      <span className="ml-auto flex h-2.5 w-2.5 relative" data-id="element-6460" data-mp="btv9269wyia5">
                        <span className="absolute inline-flex h-full w-full rounded-full bg-[#EA580C] opacity-75 animate-ping" data-id="element-6461" data-mp="2iktakz3kwmfr" />
                        <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#EA580C]" data-id="element-6462" data-mp="2dmjemma15ht3" />
                      </span>
                    </div>
                    <div className="relative z-10 space-y-4 mb-8 flex-1" data-id="element-6463" data-mp="t61qfbpscnjk">
                      {[{
                      icon: 'quickbooks',
                      text: 'Listing invoices past their due date'
                    }, {
                      icon: 'gmail',
                      text: 'Meridian #1042 — $8,400, 6 days overdue'
                    }, {
                      icon: 'gmail',
                      text: 'Atlas #1051 — $5,800, due tomorrow'
                    }, {
                      icon: 'gmail',
                      text: 'Drafting polite payment reminders per client'
                    }].map((step, i) => <GeneratingStep key={i} icon={step.icon} text={step.text} index={i} data-id="element-6464" data-mp="5duxkmj9qyow" data-mpi={i} />)}
                    </div>
                    <div className="relative z-10 bg-[#EA580C] rounded-2xl p-6 md:p-8 mt-auto shadow-xl shadow-orange-900/30" data-id="element-6465" data-mp="1ust48njxt9yf">
                      <span className="block text-[11px] font-bold uppercase tracking-[0.2em] text-white/80 mb-3" data-id="element-6466" data-mp="1zrm77m1llikc">
                        The payoff
                      </span>
                      <p className="text-xl md:text-2xl font-bold text-white leading-snug" data-id="element-6467" data-mp="1taue5ve6y1rt">
                        $14,200 outstanding across 2 invoices, with reminders
                        drafted and ready to send.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Role-relevant social proof right before the conversion moment */}
      <UseCaseTestimonials testimonials={TESTIMONIALS} data-id="element-6468" data-mp="27bevso8ddrjq" />

      {/* Keep visitors in the funnel: other use cases, then the conversion CTA */}
      <UseCaseCrossLinks current="client-management" data-id="element-6469" data-mp="239jzjy5aq4ct" />
      <ClosingCTA data-id="element-6470" data-mp="2zrpqd23jlnw5" />
    </>;
}